import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, ShoppingBag, Shield, Sparkles, Tag, Truck, Lock, ChevronLeft } from 'lucide-react';
import FallbackImage from '../../components/FallbackImage';

// Robust brand-logo chain (Wikimedia first — it reliably resolves; Clearbit/others as fallbacks).
const TARGET_LOGO_SRCS = [
    'https://upload.wikimedia.org/wikipedia/commons/c/c5/Target_Corporation_logo_%28vector%29.svg',
    'https://logo.clearbit.com/target.com',
    'https://1000logos.net/wp-content/uploads/2017/06/Target-Logo.png'
];

// Real product photography (Unsplash CDN — license-safe, stable hotlinking).
// Cart total ($97.96) is fully covered by the demo gift card ($100.00).
const cartItems = [
    {
        id: 1,
        name: "Wireless Over-Ear Headphones",
        brand: "heyday™",
        price: 49.99,
        quantity: 1,
        image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=400&h=400&q=80"
    },
    {
        id: 2,
        name: "Reversible Microfiber Comforter Set",
        brand: "Room Essentials™",
        price: 24.99,
        quantity: 1,
        image: "https://images.unsplash.com/photo-1616627561839-074385245ff6?auto=format&fit=crop&w=400&h=400&q=80"
    },
    {
        id: 3,
        name: "Sandalwood + Vanilla Soy Candle",
        brand: "Threshold™",
        price: 12.99,
        quantity: 1,
        image: "https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format&fit=crop&w=400&h=400&q=80"
    },
    {
        id: 4,
        name: "Stoneware Coffee Mug, 15 oz",
        brand: "Threshold™",
        price: 9.99,
        quantity: 1,
        image: "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?auto=format&fit=crop&w=400&h=400&q=80"
    }
];

// Target store logo lockup (bullseye + wordmark)
const TargetLogo = ({ className = "h-9 w-9" }) => (
    <div className="flex items-center gap-2.5">
        <FallbackImage srcs={TARGET_LOGO_SRCS} alt="Target" className={`${className} object-contain`} />
        <span className="text-2xl font-extrabold tracking-tight text-[#cc0000] hidden sm:inline" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
            Target
        </span>
    </div>
);

// Order Line Component — real product thumbnail, name, brand, qty, price
const OrderLine = ({ item }) => (
    <div className="flex items-center gap-4 py-4 border-b border-slate-100 dark:border-slate-700 last:border-b-0">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 flex-shrink-0 border border-slate-200 dark:border-slate-600">
            <FallbackImage srcs={[item.image]} alt={item.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#cc0000] uppercase tracking-wide">{item.brand}</p>
            <p className="font-medium text-slate-900 dark:text-white text-sm leading-snug truncate">{item.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Qty: {item.quantity}</p>
        </div>
        <span className="font-semibold text-slate-900 dark:text-white font-display tabular-nums whitespace-nowrap">
            ${item.price.toFixed(2)}
        </span>
    </div>
);

export default function DemoStorePage() {
    const [giftCardApplied, setGiftCardApplied] = useState(false);
    const [checkedOut, setCheckedOut] = useState(false);
    const [appliedValue, setAppliedValue] = useState(0);
    const [giftCardNumber, setGiftCardNumber] = useState('');
    const [giftCardPin, setGiftCardPin] = useState('');

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const discount = giftCardApplied ? appliedValue : 0;
    const total = Math.max(0, subtotal - discount);

    // Watch for autofill from extension (backup sync; controlled onChange handles the primary path)
    useEffect(() => {
        const checkForAutofill = () => {
            const numberInput = document.getElementById('gift-card-number');
            const pinInput = document.getElementById('gift-card-pin');

            if (numberInput && pinInput && numberInput.value && pinInput.value) {
                setGiftCardNumber(numberInput.value);
                setGiftCardPin(pinInput.value);
            }
        };

        // Check periodically for extension autofill
        const interval = setInterval(checkForAutofill, 500);
        return () => clearInterval(interval);
    }, []);

    const handleApplyGiftCard = () => {
        // Simulate gift card validation
        if (giftCardNumber && giftCardPin) {
            // Normalize input (remove spaces/dashes)
            const normalizedInput = giftCardNumber.replace(/[\s-]/g, '');

            const demoCards = {
                '4111567890123456': 50.00,
                '4222987650987654': 25.00,
                '4333555551234567': 97.50,
                '4847295108367294': 100.00 // Tutorial card — matches the vendor page gift card
            };

            const cardValue = demoCards[normalizedInput] || subtotal; // Fallback to covering full cost if unknown for demo flow
            setAppliedValue(Math.min(cardValue, subtotal));
            setGiftCardApplied(true);
        }
    };

    const handleCompletePurchase = () => {
        setCheckedOut(true);
        // Signal to extension that tutorial is done so it can cleanup
        window.postMessage({ type: 'GIFT_SCOUT_TUTORIAL_COMPLETE' }, '*');
    };

    const handleRemoveGiftCard = () => {
        setGiftCardApplied(false);
        setAppliedValue(0);
        setGiftCardNumber('');
        setGiftCardPin('');
        const numberInput = document.getElementById('gift-card-number');
        const pinInput = document.getElementById('gift-card-pin');
        if (numberInput) numberInput.value = '';
        if (pinInput) pinInput.value = '';
    };

    // Success state
    if (checkedOut) {
        return (
            <div className="bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 min-h-screen flex items-center justify-center p-4 transition-colors duration-200">
                <div className="max-w-lg w-full text-center">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-card-prominent p-10 border border-slate-200 dark:border-slate-700">
                        {/* Success Animation */}
                        <div className="mb-6 relative">
                            <div className="w-24 h-24 bg-[#cc0000]/10 rounded-full mx-auto flex items-center justify-center animate-pulse">
                                <CheckCircle className="w-16 h-16 text-[#cc0000]" />
                            </div>
                            <Sparkles className="w-6 h-6 text-yellow-400 absolute top-0 right-1/4 animate-bounce" />
                            <Sparkles className="w-4 h-4 text-yellow-500 absolute top-4 left-1/4 animate-bounce delay-100" />
                        </div>

                        <div className="flex justify-center mb-4">
                            <TargetLogo className="h-10 w-10" />
                        </div>

                        <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-3 font-display">
                            Order Placed!
                        </h1>
                        <p className="text-slate-600 dark:text-slate-300 text-lg mb-2">
                            Nice work! You just paid with a gift card using Gift Scout.
                        </p>
                        {discount > 0 && (
                            <div className="inline-flex items-center gap-2 bg-[#cc0000]/10 text-[#cc0000] px-4 py-2 rounded-full text-sm font-medium mb-6">
                                <Tag className="w-4 h-4" />
                                ${discount.toFixed(2)} covered by your Target gift card!
                            </div>
                        )}

                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 mb-8 text-left">
                            <h3 className="font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-brand-500" />
                                What you learned:
                            </h3>
                            <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-2">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                                    <span>How to save gift cards from a vendor site</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                                    <span>Using one-click auto-fill at checkout</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                                    <span>Letting Gift Scout track your remaining balance</span>
                                </li>
                            </ul>
                        </div>

                        <a
                            href="/tool"
                            className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all duration-200 hover:scale-105 group font-display"
                        >
                            Go to Your Card Manager
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-slate-100 dark:bg-slate-900 min-h-screen transition-colors duration-200">
            {/* Target Store Header */}
            <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-20">
                <div className="container mx-auto max-w-6xl px-4">
                    <div className="flex items-center justify-between h-16">
                        <TargetLogo />
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                            <Lock className="w-4 h-4 text-emerald-500" />
                            <span>Secure Checkout</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Sub-header / breadcrumb */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="container mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
                    <h1 className="text-lg font-bold text-slate-900 dark:text-white font-display">Checkout</h1>
                    <span className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                        <ChevronLeft className="w-4 h-4" />
                        Back to cart
                    </span>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto max-w-6xl px-4 py-8">
                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Left: Order Review */}
                    <div className="lg:col-span-3 space-y-6">
                        {/* Delivery card */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-card border border-slate-200 dark:border-slate-700 p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                                    <Truck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                </div>
                                <div>
                                    <p className="font-semibold text-slate-900 dark:text-white">Free 2-day shipping</p>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">Arrives by {new Date(Date.now() + 2 * 864e5).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
                                </div>
                            </div>
                        </div>

                        {/* Items card */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-card border border-slate-200 dark:border-slate-700 overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                                <h2 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2 font-display">
                                    <ShoppingBag className="w-5 h-5 text-[#cc0000]" />
                                    Your Items
                                </h2>
                                <span className="text-sm text-slate-500 dark:text-slate-400">{cartItems.length} items</span>
                            </div>
                            <div className="px-6">
                                {cartItems.map(item => (
                                    <OrderLine key={item.id} item={item} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right: Order Summary + Payment */}
                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-card border border-slate-200 dark:border-slate-700 overflow-hidden sticky top-20">
                            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                                <h2 className="font-bold text-lg text-slate-900 dark:text-white font-display">Order Summary</h2>
                            </div>

                            <div className="p-6">
                                {/* Instruction Banner */}
                                <div className="mb-5 p-4 bg-[#cc0000]/[0.06] dark:bg-[#cc0000]/10 border border-[#cc0000]/20 rounded-xl">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 bg-[#cc0000] rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Sparkles className="w-4 h-4 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-slate-900 dark:text-white text-sm mb-1 font-display">
                                                Pay with your saved gift card
                                            </h3>
                                            <p className="text-sm text-slate-600 dark:text-slate-300">
                                                Click the <strong>Gift Scout bubble</strong> on the right edge of your screen, then hit <strong>"Auto-fill"</strong> to apply your Target card.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Gift Card Input Section */}
                                <div
                                    id="tutorial-gift-card-box"
                                    className={`p-5 rounded-xl border-2 transition-all duration-300 ${giftCardApplied
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700'
                                        : 'bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700 hover:border-[#cc0000]/40'
                                        }`}
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <label className="font-semibold text-slate-900 dark:text-white flex items-center gap-2 font-display">
                                            <Tag className="w-4 h-4 text-[#cc0000]" />
                                            Apply Gift Card
                                        </label>
                                        {giftCardApplied && (
                                            <button
                                                onClick={handleRemoveGiftCard}
                                                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 font-medium"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>

                                    {giftCardApplied ? (
                                        <div className="flex items-center gap-3 py-2">
                                            <CheckCircle className="w-6 h-6 text-emerald-500" />
                                            <div>
                                                <p className="font-semibold text-emerald-800 dark:text-emerald-300">
                                                    Gift Card Applied!
                                                </p>
                                                <p className="text-sm text-emerald-700 dark:text-emerald-400">
                                                    •••• •••• •••• {giftCardNumber.slice(-4)} — ${discount.toFixed(2)} applied
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div>
                                                <label htmlFor="gift-card-number" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                                                    Card Number
                                                </label>
                                                <input
                                                    type="text"
                                                    id="gift-card-number"
                                                    placeholder="Enter gift card number"
                                                    value={giftCardNumber}
                                                    onChange={(e) => setGiftCardNumber(e.target.value)}
                                                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#cc0000] focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 font-mono text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="gift-card-pin" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 uppercase tracking-wider">
                                                    PIN Code
                                                </label>
                                                <input
                                                    type="text"
                                                    id="gift-card-pin"
                                                    placeholder="Enter PIN"
                                                    value={giftCardPin}
                                                    onChange={(e) => setGiftCardPin(e.target.value)}
                                                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#cc0000] focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 font-mono text-sm"
                                                />
                                            </div>
                                            <button
                                                onClick={handleApplyGiftCard}
                                                disabled={!giftCardNumber || !giftCardPin}
                                                className="w-full py-3 bg-[#cc0000] hover:bg-[#a30000] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors font-display"
                                            >
                                                Apply Gift Card
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Pricing Breakdown */}
                                <div className="space-y-3 mt-6 pt-5 border-t border-slate-100 dark:border-slate-700">
                                    <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                                        <span>Subtotal ({cartItems.length} items)</span>
                                        <span className="tabular-nums">${subtotal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                                        <span>Shipping</span>
                                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">FREE</span>
                                    </div>
                                    {giftCardApplied && (
                                        <div className="flex justify-between text-sm text-emerald-700 dark:text-emerald-400 font-medium bg-emerald-50 dark:bg-emerald-900/20 -mx-2 px-2 py-2 rounded-lg">
                                            <span className="flex items-center gap-1">
                                                <Tag className="w-3 h-3" />
                                                Gift Card
                                            </span>
                                            <span className="tabular-nums">-${discount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between font-bold text-xl text-slate-900 dark:text-white pt-3 border-t border-slate-200 dark:border-slate-600 font-display tabular-nums">
                                        <span>Total</span>
                                        <span>${total.toFixed(2)}</span>
                                    </div>
                                </div>

                                {/* Complete Purchase Button */}
                                <button
                                    onClick={handleCompletePurchase}
                                    disabled={!giftCardApplied && total > 0}
                                    className={`w-full mt-6 py-4 px-6 rounded-full font-bold text-lg transition-all duration-200 flex items-center justify-center gap-2 font-display ${giftCardApplied || total === 0
                                        ? 'bg-[#cc0000] hover:bg-[#a30000] text-white shadow-lg hover:shadow-xl hover:scale-[1.02]'
                                        : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                                        }`}
                                >
                                    Place your order
                                    {total > 0 && ` — $${total.toFixed(2)}`}
                                </button>

                                {!giftCardApplied && (
                                    <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-3">
                                        Apply your gift card to continue
                                    </p>
                                )}

                                {/* Security Badge */}
                                <div className="mt-4 flex items-center justify-center gap-3 text-xs text-slate-400 dark:text-slate-500">
                                    <span className="flex items-center gap-1">
                                        <Shield className="w-3 h-3" />
                                        SSL Encrypted
                                    </span>
                                    <span>•</span>
                                    <span>Demo checkout</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
